import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { CereriService } from '../cereri.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UpdateMassAssignPayload, UsersByCompartimentePayload } from '../cereri.interface';
import { CommonService } from 'src/app/services/common.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { notDefaultValidator } from 'src/app/validators/not-default';

@Component({
    selector: 'app-cereri-asigneaza',
    templateUrl: './cereri-asigneaza.component.html',
    styleUrls: ['./cereri-asigneaza.component.scss']
})
export class CereriAsigneazaComponent implements OnInit, OnDestroy {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    isLoading = false;

    defaultParameter: { name: string; id: number } = {
        name: '-- Alegeți --',
        id: -1,
    };

    // directii$!: Observable<Generic[]>;
    compartimente$: any[] = this.data.directii;
    usersActive!: Generic[];

    private asignareDestroy$ = new Subject<void>();

    filteredCompartimente: any[] = this.data.directii;
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    form: FormGroup = new FormGroup({
        directie_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        operator: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        private dialogRef: MatDialogRef<CereriAsigneazaComponent>,
        private cereriService: CereriService,
        private notificationService: NotificationService,
        private commonService: CommonService,
    ) { }

    ngOnInit(): void {
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.asignareDestroy$.next();
        this.asignareDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    renderFieldsSearch() {
        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.asignareDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimente$;
                    return of(this.compartimente$);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.asignareDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimente$.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(categorie =>
                categorie.id_compartiment === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimente$;
                this.asignareDestroy$.next();
                this.asignareDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.form.controls['operator'].patchValue(-1);

        if (compartimentId == -1) {
            this.usersActive = [];
            this.filteredUsers = [];
            return;
        }

        if (compartimentId) {
            this.loadUsers(compartimentId);
        }
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.form.get('operator')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.asignareDestroy$.next();
                this.asignareDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadUsers(compartimentId: number) {
        this.isLoading = true;
        let payload: UsersByCompartimentePayload = { compartiment_id: compartimentId };

        let usersObservable$ = compartimentId
            ? this.cereriService.getActiveUsersByCompartiment(payload)
            : this.commonService.getUsersActive();

        usersObservable$.pipe(
            map((res: any) => [...res]),
            finalize(() => (this.isLoading = false)),
        ).subscribe({
            next: (users) => {
                this.usersActive = [...users];
                this.filteredUsers = [...users];
            },
            error: (err) => {
                console.log(err);
                this.filteredUsers = [];
            }
        });
    }

    submitForm() {
        this.isLoading = true;

        const ids_cerere = this.data.selectedRowIds;
        const directie_id = this.form.get('directie_id')?.value;
        const user_id = this.form.get('operator')?.value;

        const newAssign: UpdateMassAssignPayload = {
            ids_cerere,
            directie_id: directie_id,
            user_id: user_id,
        };

        this.cereriService
            .updateMassAssign(newAssign)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
